'use client';

import { Button } from '@/components/ui/button';
import { AnalyticsEvent } from '@/shared/lib/analytics/events';
import { isProfileComplete } from '@/shared/lib/auth/profile-completion';
import { useAuthStore } from '@/shared/stores/auth-store';
import type { User } from '@/types';
import { Loader2, Shield } from 'lucide-react';
import posthog from 'posthog-js';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';

const ADMIN_EMAIL = process.env.NEXT_PUBLIC_ADMIN_EMAIL;
const IS_DEV = process.env.NODE_ENV === 'development';

interface ImpersonateButtonProps {
  userId: number;
  handler: string;
}

export function ImpersonateButton({ userId, handler }: ImpersonateButtonProps) {
  const userEmail = useAuthStore((s) => s.user?.email);
  const [isPending, setIsPending] = useState(false);

  const isAdmin = !!ADMIN_EMAIL && !!userEmail && userEmail === ADMIN_EMAIL;

  const handleImpersonate = useCallback(async () => {
    setIsPending(true);
    try {
      const res = await fetch('/api/admin/impersonate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        if (res.status === 403) {
          toast.error('Not authorized to impersonate');
        } else if (res.status === 404) {
          toast.error('User not found');
        } else {
          toast.error(
            (data as Record<string, unknown> | null)?.message?.toString() || 'Failed to impersonate'
          );
        }
        setIsPending(false);
        return;
      }

      const data = await res.json();
      const user = data.user as User;
      const subscribed = !!(data.subscribed as boolean | undefined);

      // Capture admin email BEFORE localStorage is rewritten with the
      // impersonated user — otherwise impersonator_email leaks as the creator's.
      const impersonatorEmail = useAuthStore.getState().user?.email;

      if (!IS_DEV) {
        const superProps = {
          is_impersonation: true,
          impersonated_user_id: user.id,
          impersonated_user_email: user.email,
          impersonated_user_handle: handler,
          impersonation_started_at: new Date().toISOString(),
          ...(impersonatorEmail ? { impersonator_email: impersonatorEmail } : {}),
        };
        posthog.register(superProps);
        posthog.capture(AnalyticsEvent.ADMIN_IMPERSONATION_STARTED, superProps, {
          send_instantly: true,
        });
      }

      // Write impersonated user directly to Zustand persist storage
      // so rehydration on the next page loads correct data immediately
      localStorage.setItem(
        'tabootv-auth',
        JSON.stringify({
          state: {
            user,
            isSubscribed: subscribed,
            isProfileComplete: isProfileComplete(user),
            isAuthenticated: true,
          },
          version: 0,
        })
      );

      sessionStorage.setItem('impersonating', handler);
      window.location.href = '/';
    } catch {
      toast.error('Failed to impersonate');
      setIsPending(false);
    }
  }, [userId, handler]);

  if (!isAdmin) return null;

  return (
    <Button
      variant="outline"
      size="sm"
      disabled={isPending}
      onClick={handleImpersonate}
      className="bg-amber-700 hover:bg-amber-700/50 btn btn-sm h-9 px-8 py-3 text-sm font-semibold rounded min-w-[140px]"
    >
      {isPending ? <Loader2 className="size-4 animate-spin" /> : <Shield className="size-4" />}
      Impersonate
    </Button>
  );
}
